import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { TableCellDisplay } from '@/components/ui-v2/inputs/table/table-integer-display';
import { TableIntegerInput } from '@/components/ui-v2/inputs/table/table-integer-input';
import { useIsCellEditing } from '@/hooks/use-is-cell-editing';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useToast } from '@/hooks/use-toast';
import {
  DealItem,
  DealItemWithVersions,
} from '@/lib/graphql/generated/graphql';
import { useMoveProductsToPosition } from '@/lib/hooks/useMoveProductsToPosition';
import {
  isDealItemCategoryUpdating,
  resolveDealItemFromMap,
} from '@/lib/utils/updating-fields';
import { useSetColumnSort } from '@/states/column-sort.state';
import { setEditingCellAtom } from '@/states/editing-cell.state';
import { productsMapState } from '@/states/products.state';
import { Row } from '@tanstack/react-table';
import { useAtomValue, useSetAtom } from 'jotai';

import { ExtendedColumnDef } from '../table-products';
import { SortIcon } from './sort';

interface ColumnProps {
  dealId: string;
  handleCellClick: (id: string, field: keyof DealItem) => void;
}

interface PositionCellContentProps {
  row: Row<DealItemWithVersions>;
  handleCellClick: ColumnProps['handleCellClick'];
  moveProductsToPosition: (
    productGroupKeys: string[],
    targetPosition: number
  ) => Promise<unknown>;
}

function PositionCellContent({
  row,
  handleCellClick,
  moveProductsToPosition,
}: PositionCellContentProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const setEditingCell = useSetAtom(setEditingCellAtom);
  const isOfferLocked = useIsOfferLocked();
  const productsMap = useAtomValue(productsMapState);
  const cancelRef = useRef(false);
  const isEditing = useIsCellEditing(row, 'position', true);
  const isChild = typeof row.parentId !== 'undefined';
  const categoryUpdating = isDealItemCategoryUpdating(
    resolveDealItemFromMap(productsMap, row.original)
  );

  if (isChild) {
    return (
      <div className="flex h-full w-full items-center justify-center px-2 text-sm text-gray-400">
        —
      </div>
    );
  }

  const positionValue = row.original.activeVersion.position ?? null;

  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (e) => {
    if (e.key === 'Escape') {
      cancelRef.current = true;
      setEditingCell(null);
      e.currentTarget.blur();
    } else if (e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  const handleBlur: React.FocusEventHandler<HTMLInputElement> = () => {
    setEditingCell(null);
  };

  const handleChange = async (value: number | null) => {
    if (cancelRef.current) {
      cancelRef.current = false;
      return;
    }
    if (categoryUpdating) {
      setEditingCell(null);
      return;
    }
    if (value === null || value < 1 || !Number.isInteger(value)) {
      toast({
        title: t('kpi.position_invalid'),
        variant: 'destructive',
      });
      return;
    }
    const productGroupKey = row.original.activeVersion.productGroupKey;
    await moveProductsToPosition([productGroupKey], value);
    setEditingCell(null);
  };

  return (
    <div className="h-full w-full">
      {isEditing ? (
        <TableIntegerInput
          value={positionValue}
          onChange={handleChange}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          disabled={isOfferLocked || categoryUpdating}
          autoFocus
        />
      ) : (
        <TableCellDisplay
          value={positionValue}
          align="center"
          isEditable={!isOfferLocked && !categoryUpdating}
          isParent={false}
          onClick={(e) => {
            e.stopPropagation();
            if (!isOfferLocked && !categoryUpdating) {
              handleCellClick(row.original.activeVersion.id, 'position');
            }
          }}
        />
      )}
    </div>
  );
}

const useColumnDefPosition = ({
  dealId,
  handleCellClick,
}: ColumnProps): ExtendedColumnDef<DealItemWithVersions> => {
  const { t } = useTranslation();
  const { setColumnSort } = useSetColumnSort();
  const { moveProductsToPosition } = useMoveProductsToPosition(dealId);

  const accessorKey: keyof DealItem = 'position';

  return useMemo(
    () => ({
      accessorKey,
      name: 'Position',
      accessorFn: (row) => row.activeVersion?.position ?? null,
      aggregationFn: 'mean',
      sortingFn: 'basic',
      header: () => (
        <div
          className="flex w-full items-center justify-center gap-1"
          onClick={() => setColumnSort(accessorKey)}
        >
          {t('kpi.position_short')}
          <SortIcon pointer="position" />
        </div>
      ),
      cell: ({ row }) => (
        <PositionCellContent
          row={row}
          handleCellClick={handleCellClick}
          moveProductsToPosition={moveProductsToPosition}
        />
      ),
      size: 70,
    }),
    [t, setColumnSort, moveProductsToPosition, handleCellClick]
  );
};

export default useColumnDefPosition;
